import { useState } from 'react'
import { useRouter } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faBoxOpen, faRotateRight } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import { Footer, HeaderMiddle, Navbar, TopBar, WA_NUMBER } from './SiteChrome'
import type { StorefrontConfig } from '../lib/dropify'
import { whatsAppCtaProps } from '../lib/analytics'
import { absoluteUrl } from '../lib/seo'

export function UpstreamUnavailablePage({
  config,
  path,
  eyebrow,
  title,
  description,
  backHref,
  backLabel,
}: {
  config: StorefrontConfig | null
  path: string
  eyebrow: string
  title: string
  description: string
  backHref: string
  backLabel: string
}) {
  const router = useRouter()
  const [retrying, setRetrying] = useState(false)
  const message = `Halo Dropify, saya membuka ${absoluteUrl(path)}, tetapi detail halamannya sedang diperbarui. Saya ingin menanyakan produk: ___. Jumlah: ___. Kota tujuan: ___.`

  async function retry() {
    setRetrying(true)
    try {
      await router.invalidate()
    } catch {
      // Keep the recovery page interactive while the route is still unavailable.
    } finally {
      setRetrying(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar config={config} />
      <HeaderMiddle />
      <Navbar />

      <main>
        <section className="relative overflow-hidden bg-slate-950 text-white">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.28),transparent_42%)]" />
          <div className="relative mx-auto max-w-5xl px-4 py-16 md:py-24">
            <span className="inline-flex rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-200">
              {eyebrow}
            </span>
            <h1 className="mt-5 max-w-3xl text-3xl font-black leading-tight tracking-tight md:text-5xl">{title}</h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">{description}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={retry}
                disabled={retrying}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-500 disabled:cursor-wait disabled:bg-blue-400"
              >
                <FontAwesomeIcon icon={faRotateRight} className={retrying ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                {retrying ? 'Memeriksa kembali...' : 'Coba muat ulang'}
              </button>
              <a
                href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`}
                target="_blank"
                rel="noopener noreferrer"
                {...whatsAppCtaProps('sitewide', { cta_placement: 'upstream_unavailable' })}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-green-700 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-green-600"
              >
                <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5" />
                Tanyakan kepada admin
              </a>
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-5xl gap-4 px-4 py-10 md:grid-cols-2 md:py-14">
          <a href={backHref} className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:border-blue-300 hover:shadow-md">
            <FontAwesomeIcon icon={faArrowLeft} className="h-5 w-5 text-blue-600" />
            <h2 className="mt-4 text-lg font-black text-slate-900">{backLabel}</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">Buka halaman daftar untuk melihat pilihan lain yang sudah tersedia.</p>
          </a>
          <a href="/katalog" className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:border-blue-300 hover:shadow-md">
            <FontAwesomeIcon icon={faBoxOpen} className="h-5 w-5 text-blue-600" />
            <h2 className="mt-4 text-lg font-black text-slate-900">Buka katalog produk</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">Cari kategori atau produk, lalu kirim nama dan jumlah yang dibutuhkan melalui WhatsApp.</p>
          </a>
        </section>
      </main>

      <Footer config={config} />
    </div>
  )
}

export function UpstreamUnavailablePanel({
  path,
  title,
  description,
}: {
  path: string
  title: string
  description: string
}) {
  const router = useRouter()
  const [retrying, setRetrying] = useState(false)
  const message = `Halo Dropify, saya membuka ${absoluteUrl(path)}, tetapi daftar kontennya sedang diperbarui. Saya ingin menanyakan: ___.`

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" aria-labelledby="temporary-content-title">
      <div className="h-1.5 bg-gradient-to-r from-blue-600 via-blue-500 to-amber-400" />
      <div className="px-6 py-10 text-center md:px-10 md:py-14">
        <p className="text-xs font-bold uppercase tracking-wider text-blue-700">Pembaruan sementara</p>
        <h2 id="temporary-content-title" className="mt-3 text-2xl font-black tracking-tight text-slate-900">{title}</h2>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-600 md:text-base">{description}</p>
        <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
          <button
            type="button"
            disabled={retrying}
            onClick={async () => {
              setRetrying(true)
              try {
                await router.invalidate()
              } catch {
                // The current panel remains useful until the next successful retry.
              } finally {
                setRetrying(false)
              }
            }}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-700 disabled:cursor-wait disabled:bg-blue-400"
          >
            <FontAwesomeIcon icon={faRotateRight} className={retrying ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            {retrying ? 'Memeriksa kembali...' : 'Coba muat ulang'}
          </button>
          <a
            href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`}
            target="_blank"
            rel="noopener noreferrer"
            {...whatsAppCtaProps('sitewide', { cta_placement: 'upstream_unavailable_panel' })}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-green-700 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-green-800"
          >
            <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5" />
            Hubungi admin
          </a>
        </div>
      </div>
    </section>
  )
}
